/**
 * Pending Job / QC Batch selection — shared by the three manual result forms
 * (RTCFR "REE Redesign — Pending Job / QC Batch Selection", 2026-09-27; applied
 * to Result Validation + Result Approval by "Apply Consolidated REE Design to
 * Result Validation & Result Approval", 2026-09-28).
 *
 * Result Entry Express, Result Validation and Result Approval no longer open on an
 * empty result grid: they first list what is PENDING at their own stage in two
 * tabs (Sample = Job IDs, QC Batch = QC Batch IDs) for the current Date Range +
 * Query Panel, and the form's own detail grid appears only after Load.
 *
 * Pure helpers only — NO new data. Each form feeds them its EXISTING list API
 * (the Sample tab) and its EXISTING facet-rows API (the QC Batch tab):
 *   • REE — /result-entry + /result-entry/facet-rows (per-sample rows that carry
 *     their QC batch → buildPendingBatches, exact pending-sample counts);
 *   • RV / RA — /analysis/{manual-result-validation|result-approval} + their
 *     /facet-rows (job rows + QC Batch↔Job edges → buildPendingBatchesFromEdges).
 */
import type { AppColumn } from '../../components/AppDataGrid';
import { fmtLocalDateTime } from '../../utils/datetime';
import { fuzzyMatch } from '../../utils/fuzzyMatch';

/** One distinct dimension-combination of the pending-entry universe, from
 *  /api/v1/result-entry/facet-rows (AI_Analysis_ResultEntry_FacetRows_SP). */
export type ReeFacetRow = {
  qcBatchID: string | null; jobID: string | null; client: string | null;
  projectID: string | null; projectName: string | null; test: string | null;
  matrix: string | null; sampleLocation: string | null;
  batchTest: string | null; batchEmp: string | null; batchMethod: string | null;
  sampleOid: string | null;
  receivedDate: string | null; collectDate: string | null; analyzedDate: string | null;
};

/** One pending job in the Sample tab — the columns every form's job list shares.
 *  REE's ResultEntryJob and the RV/RA list rows both provide these fields. */
export interface PendingJob {
  jobID:        string;
  projectID:    string | null;
  projectName:  string | null;
  client:       string | null;
  receivedDate: string | null;
  /** The row's pending status at THIS form's stage (Pending Entry / Validation / Approval). */
  statusText:   string;
}

/** One pending QC batch in the selection panel's QC Batch tab. */
export interface ReePendingBatch {
  qcBatchID:      string;
  test:           string;
  method:         string;
  matrix:         string;
  client:         string;
  projectID:      string;
  jobIDs:         string[];
  /** Distinct pending samples in the batch, or null when the form's facet has no
   *  per-sample batch link (RV/RA — then the column is hidden, never guessed). */
  pendingSamples: number | null;
  statusText:     string;
}

/** Stage status shown on a QC batch row — the facet carries no batch status of its
 *  own; every listed batch is, by construction, pending at the form's stage. */
export const PENDING_ENTRY_STATUS      = 'Pending Entry';
export const PENDING_VALIDATION_STATUS = 'Pending Validation';
export const PENDING_APPROVAL_STATUS   = 'Pending Approval';

export type ReeSelTab = 'Sample' | 'QC Batch';
export const REE_SEL_TABS: ReeSelTab[] = ['Sample', 'QC Batch'];

/**
 * Sample tab rows: the pending job list narrowed to the jobs present in the facet
 * rows that match the Query Panel. With no filter active the full pending list is
 * shown unchanged — so a facet fetch failure never empties the tab.
 */
export function filterPendingJobs<T extends { jobID: string }>(
  pendingRows: readonly T[],
  matchedFacet: readonly { jobID: string | null }[],
  hasFilter: boolean,
): T[] {
  if (!hasFilter) return [...pendingRows];
  const jobs = new Set<string>();
  for (const r of matchedFacet) if (r.jobID) jobs.add(r.jobID);
  return pendingRows.filter(j => jobs.has(j.jobID));
}

const byNatural = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
const joined = (s: Set<string>) => Array.from(s).sort(byNatural).join('; ');

/**
 * REE QC Batch tab rows: the matched facet rows grouped by QCBatchID. Multi-valued
 * dimensions (a batch spanning several jobs / clients) are shown '; '-joined.
 * Pending Samples = DISTINCT sampleOid — a sample repeats once per pending test
 * and per linked batch in the facet, so summing rows would over-count.
 */
export function buildPendingBatches(
  matchedFacet: readonly ReeFacetRow[],
  statusText: string = PENDING_ENTRY_STATUS,
): ReePendingBatch[] {
  type Acc = {
    test: string; method: string;
    matrix: Set<string>; client: Set<string>; projectID: Set<string>;
    jobs: Set<string>; samples: Set<string>;
  };
  const acc = new Map<string, Acc>();
  for (const r of matchedFacet) {
    if (!r.qcBatchID) continue;
    let a = acc.get(r.qcBatchID);
    if (!a) {
      a = { test: '', method: '', matrix: new Set(), client: new Set(), projectID: new Set(), jobs: new Set(), samples: new Set() };
      acc.set(r.qcBatchID, a);
    }
    if (!a.test && r.batchTest) a.test = r.batchTest;
    if (!a.method && r.batchMethod) a.method = r.batchMethod;
    if (r.matrix)    a.matrix.add(r.matrix);
    if (r.client)    a.client.add(r.client);
    if (r.projectID) a.projectID.add(r.projectID);
    if (r.jobID)     a.jobs.add(r.jobID);
    if (r.sampleOid) a.samples.add(r.sampleOid);
  }
  return Array.from(acc, ([qcBatchID, a]) => ({
    qcBatchID,
    test: a.test,
    method: a.method,
    matrix: joined(a.matrix),
    client: joined(a.client),
    projectID: joined(a.projectID),
    jobIDs: Array.from(a.jobs).sort(byNatural),
    pendingSamples: a.samples.size,
    statusText,
  }));
}

/** A QC Batch↔Job edge from the RV/RA facet-rows APIs. */
export type PendingBatchEdge = {
  qcBatchID: string | null; jobID: string | null;
  batchTest: string | null; batchMethod: string | null;
};
/** A job-side facet row from the RV/RA facet-rows APIs (one per job × sample combination). */
export type PendingFacetJob = {
  jobID: string | null; client: string | null; projectID: string | null; matrix: string | null;
};

/**
 * RV / RA QC Batch tab rows. Their facet links a QC batch to its JOBS (edges), not
 * to individual samples, so a batch row lists the pending jobs it rides on —
 * restricted to the jobs matching the Query Panel (`matchedJobs`) — with its own
 * Test / Method from the edge and Matrix / Client / Project from those jobs.
 * A Test filter keeps only batches OF that test and a QC Batch ID filter only the
 * chosen batches, mirroring the Query Panel's own QC Batch picker cascade.
 * Pending Samples is null (unknowable at batch↔job grain — the column is hidden).
 */
export function buildPendingBatchesFromEdges(
  edges: readonly PendingBatchEdge[],
  matchedJobs: readonly PendingFacetJob[],
  statusText: string,
  filter: { test: readonly string[]; batchId: readonly string[] },
): ReePendingBatch[] {
  const jobInfo = new Map<string, { matrix: Set<string>; client: Set<string>; projectID: Set<string> }>();
  for (const j of matchedJobs) {
    if (!j.jobID) continue;
    let info = jobInfo.get(j.jobID);
    if (!info) { info = { matrix: new Set(), client: new Set(), projectID: new Set() }; jobInfo.set(j.jobID, info); }
    if (j.matrix)    info.matrix.add(j.matrix);
    if (j.client)    info.client.add(j.client);
    if (j.projectID) info.projectID.add(j.projectID);
  }
  type Acc = { test: string; method: string; matrix: Set<string>; client: Set<string>; projectID: Set<string>; jobs: Set<string> };
  const acc = new Map<string, Acc>();
  for (const e of edges) {
    if (!e.qcBatchID || !e.jobID) continue;
    const info = jobInfo.get(e.jobID);
    if (!info) continue;                                                  // job not in the matched set
    if (filter.test.length && !(e.batchTest && filter.test.includes(e.batchTest))) continue;
    if (filter.batchId.length && !filter.batchId.includes(e.qcBatchID)) continue;
    let a = acc.get(e.qcBatchID);
    if (!a) { a = { test: '', method: '', matrix: new Set(), client: new Set(), projectID: new Set(), jobs: new Set() }; acc.set(e.qcBatchID, a); }
    if (!a.test && e.batchTest) a.test = e.batchTest;
    if (!a.method && e.batchMethod) a.method = e.batchMethod;
    a.jobs.add(e.jobID);
    info.matrix.forEach(v => a!.matrix.add(v));
    info.client.forEach(v => a!.client.add(v));
    info.projectID.forEach(v => a!.projectID.add(v));
  }
  return Array.from(acc, ([qcBatchID, a]) => ({
    qcBatchID,
    test: a.test,
    method: a.method,
    matrix: joined(a.matrix),
    client: joined(a.client),
    projectID: joined(a.projectID),
    jobIDs: Array.from(a.jobs).sort(byNatural),
    pendingSamples: null,
    statusText,
  }));
}

/** Selection-panel fuzzy search: a row matches when the query fuzzy-matches ANY one
 *  of its columns' displayed text (the shared canonical matcher, BO UI Standard §9)
 *  — matched per field, not across the joined row, so characters can't be stitched
 *  together from two different columns. Blank query = every row. */
export function searchRows<T>(rows: readonly T[], cols: readonly AppColumn<T>[], query: string): T[] {
  const q = query.trim();
  if (!q) return rows.slice();
  return rows.filter(r => cols.some(c => fuzzyMatch(q, c.text ? c.text(r) : '')));
}

/** Sample tab columns — the legacy job-selection list (Job ID · Project ID ·
 *  Project Name · Client · Received Date · Status). */
export function pendingJobCols(): AppColumn<PendingJob>[] {
  return [
    { field: 'jobID',        header: 'JobID',        width: 120, text: r => r.jobID ?? '' },
    { field: 'projectID',    header: 'ProjectID',    text: r => r.projectID ?? '' },
    { field: 'projectName',  header: 'ProjectName',  text: r => r.projectName ?? '' },
    { field: 'client',       header: 'Client',       text: r => r.client ?? '' },
    { field: 'receivedDate', header: 'ReceivedDate', type: 'datetime', width: 140,
      value: r => r.receivedDate, text: r => fmtLocalDateTime(r.receivedDate) },
    { field: 'statusText',   header: 'Status',       type: 'status', text: r => r.statusText ?? '' },
  ];
}

/** QC Batch tab columns. `showPendingSamples` = false drops the count column for a
 *  form whose facet can't compute it (RV / RA). */
export function pendingBatchCols(showPendingSamples = true): AppColumn<ReePendingBatch>[] {
  return [
    { field: 'qcBatchID',      header: 'QCBatchID',       width: 130, text: r => r.qcBatchID },
    { field: 'test',           header: 'Test',            text: r => r.test },
    { field: 'method',         header: 'Method',          text: r => r.method },
    { field: 'matrix',         header: 'Matrix',          text: r => r.matrix },
    { field: 'client',         header: 'Client',          text: r => r.client },
    { field: 'projectID',      header: 'ProjectID',       text: r => r.projectID },
    { field: 'jobIDs',         header: 'JobID',           text: r => r.jobIDs.join('; ') },
    ...(showPendingSamples ? [{
      field: 'pendingSamples', header: 'Pending Samples', type: 'number' as const, width: 120,
      value: (r: ReePendingBatch) => r.pendingSamples, text: (r: ReePendingBatch) => String(r.pendingSamples ?? ''),
    }] : []),
    { field: 'statusText',     header: 'Status',          type: 'status', text: r => r.statusText },
  ];
}
